// src/store/selectors/index.ts
import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../index';

// Auth selectors
export const selectAuth = (state: RootState) => state.auth;
export const selectUser = (state: RootState) => state.auth.user;
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
export const selectAuthLoading = (state: RootState) => state.auth.isLoading;
export const selectAuthError = (state: RootState) => state.auth.error;

// Organization selectors
export const selectOrganization = (state: RootState) => state.organization;
export const selectCurrentOrganization = (state: RootState) => state.organization.organization;
export const selectOrganizationLoading = (state: RootState) => state.organization.isLoading;
export const selectShowSetupModal = (state: RootState) => state.organization.showSetupModal;

// Branch selectors
export const selectBranch = (state: RootState) => state.branch;
export const selectBranches = (state: RootState) => state.branch.branches;
export const selectCurrentBranch = (state: RootState) => state.branch.currentBranch;
export const selectBranchCounts = (state: RootState) => state.branch.branchCounts;
export const selectBranchLoading = (state: RootState) => state.branch.isLoading;
export const selectHasChanges = (state: RootState) => state.branch.hasChanges;
export const selectIsPublishing = (state: RootState) => state.branch.isPublishing;

// Category selectors
export const selectCategory = (state: RootState) => state.category;
export const selectCategories = (state: RootState) => state.category.categories;
export const selectMainCategories = (state: RootState) => state.category.mainCategories;
export const selectSubCategories = (state: RootState) => state.category.subCategories;
export const selectCategoriesHierarchy = (state: RootState) => state.category.categoriesHierarchy;
export const selectSelectedCategory = (state: RootState) => state.category.selectedCategory;
export const selectCategoryLoading = (state: RootState) => state.category.isLoading;
// Cache metadata selectors for categories
export const selectCategoryLastFetchedAt = (state: RootState) => state.category.lastFetchedAt;
export const selectCategoryCurrentBranchId = (state: RootState) => state.category.currentBranchId;

// Create memoized selector for subcategories by parent ID
export const selectSubCategoriesByParentId = createSelector(
	[selectSubCategories, (state: RootState, parentId: string) => parentId],
	(subCategories, parentId) => subCategories.filter(cat => cat.parent_id === parentId)
);

// Create memoized selector for published categories only
export const selectPublishedCategories = createSelector(
	[selectCategories],
	(categories) => categories.filter(cat => cat.is_published)
);

// Menu item selectors
export const selectMenuItem = (state: RootState) => state.menuItem;
export const selectMenuItems = (state: RootState) => state.menuItem.menuItems;
export const selectFilteredMenuItems = (state: RootState) => state.menuItem.filteredItems;
export const selectSelectedMenuItem = (state: RootState) => state.menuItem.selectedMenuItem;
export const selectMenuItemLoading = (state: RootState) => state.menuItem.isLoading;
export const selectMenuItemHasFilters = (state: RootState) => !!state.menuItem.searchQuery || !!state.menuItem.selectedCategoryFilter || state.menuItem.statusFilter !== 'all';
export const selectMenuItemFilters = createSelector(
	[selectMenuItem],
	(menuItem) => ({
		searchQuery: menuItem.searchQuery,
		categoryFilter: menuItem.selectedCategoryFilter,
		statusFilter: menuItem.statusFilter,
	})
);
export const selectMenuItemLoadingAnalytics = (state: RootState) => state.menuItem.isLoadingAnalytics;
export const selectMenuItemsAnalytics = (state: RootState) => state.menuItem.analytics;
// Cache metadata selectors for menu items
export const selectMenuItemLastFetchedAt = (state: RootState) => state.menuItem.lastFetchedAt;
export const selectMenuItemCurrentBranchId = (state: RootState) => state.menuItem.currentBranchId;

// Create memoized selector for menu items by category
export const selectMenuItemsByCategory = createSelector(
	[selectMenuItems, (state: RootState, categoryId: string) => categoryId],
	(menuItems, categoryId) => menuItems.filter(item => item.category_id === categoryId)
);

// Create memoized selector for featured menu items
export const selectFeaturedMenuItems = createSelector(
	[selectMenuItems],
	(menuItems) => menuItems.filter(item => item.is_featured && item.is_published && item.is_available)
);

// UI selectors
export const selectUI = (state: RootState) => state.ui;
export const selectSidebarOpen = (state: RootState) => state.ui.sidebarOpen;
export const selectSidebarCollapsed = (state: RootState) => state.ui.sidebarCollapsed;
export const selectModals = (state: RootState) => state.ui.modals;
export const selectViewModes = createSelector(
	[selectUI],
	(ui) => ({
		menuItems: ui.menuItemsViewMode,
		categories: ui.categoriesViewMode,
	})
);
export const selectLoadingStates = (state: RootState) => state.ui.loadingStates;
export const selectNotifications = (state: RootState) => state.ui.notifications;
export const selectActiveSettingsTab = (state: RootState) => state.ui.activeSettingsTab;
export const selectFiltersVisible = (state: RootState) => state.ui.filtersVisible;

// Combined selectors for dashboard
export const selectDashboardData = createSelector(
	[selectCurrentBranch, selectBranchCounts, selectMainCategories, selectMenuItems],
	(branch, counts, categories, menuItems) => ({
		branch,
		counts,
		categoriesCount: categories.length,
		menuItemsCount: menuItems.length,
		publishedItemsCount: menuItems.filter((item: any) => item.is_published).length,
		availableItemsCount: menuItems.filter((item: any) => item.is_available).length,
	})
);

// Loading state combinations
export const selectIsPageLoading = createSelector(
	[selectAuthLoading, selectOrganizationLoading, selectBranchLoading],
	(authLoading, orgLoading, branchLoading) => authLoading || orgLoading || branchLoading
);

export const selectIsDataLoading = createSelector(
	[selectCategoryLoading, selectMenuItemLoading],
	(categoryLoading, menuItemLoading) => categoryLoading || menuItemLoading
);

// Dietary preferences selectors
export const selectDietary = (state: RootState) => state.dietary;
export const selectDietaryPreferences = (state: RootState) => state.dietary.preferences;
export const selectDietaryLoading = (state: RootState) => state.dietary.isLoading;
export const selectDietaryError = (state: RootState) => state.dietary.error;

// Currency selectors
export const selectCurrency = (state: RootState) => state.currency;
export const selectCurrencies = (state: RootState) => state.currency.currencies;
export const selectSelectedCurrencyId = (state: RootState) => state.currency.selectedCurrencyId;
export const selectCurrencyLoading = (state: RootState) => state.currency.isLoading;
export const selectCurrencyError = (state: RootState) => state.currency.error;

// Memoized selector for the selected currency object
export const selectSelectedCurrency = createSelector(
	[selectCurrencies, selectSelectedCurrencyId],
	(currencies, selectedId) => currencies.find(c => c.id === selectedId) || null
);